import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnimationFrame } from '../useAnimationFrame';
import {
  VizCard,
  NowYouTry,
  VIZ_FOCUS,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// OrbitalShapes — the SHAPES of atomic orbitals, the way they appear in a
// "Orbital types in energy levels" chart: the single s sphere, the three p
// dumbbells (px, py, pz along the axes), the five d cloverleaves (incl. the
// dz² ring-and-lobes), and the seven f orbitals (shown as labelled emblems).
// A 3D-ish isometric x/y/z axis frame anchors every orbital so the orientation
// reads at a glance. A selector switches s/p/d/f; the group rotates gently
// (static under reduced motion). config: { sublevel?, title? }.
// Cue: cue.highlight names a sublevel ('p') or a specific orbital ('px','dz2')
// to spotlight; cue.step walks the orbitals of the current sublevel; and
// cue.progress sweeps the selection s→p→d→f.

export type Sublevel = 's' | 'p' | 'd' | 'f';

export interface OrbitalShapesConfig {
  /** Which sublevel to open on. Omit to cycle all in the self-demo. */
  sublevel?: Sublevel;
  title?: string;
}

interface Props {
  config?: OrbitalShapesConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const SUBLEVELS: Sublevel[] = ['s', 'p', 'd', 'f'];

// Each orbital we can draw: an id used for cue matching, a short label, and the
// kind of glyph to render. Lobe orbitals carry a rotation (deg) and whether the
// dumbbell points along the depth (z) axis so we can foreshorten it.
type Glyph =
  | { kind: 'sphere' }
  | { kind: 'dumbbell'; rot: number; depth?: boolean }
  | { kind: 'clover'; rot: number }
  | { kind: 'donut' } // dz²: torus + vertical lobes
  | { kind: 'emblem'; lobes: number }; // f orbitals — multi-lobe emblem

interface Orbital {
  id: string;
  label: string;
  glyph: Glyph;
}

// The orbitals that make up each sublevel. Counts: s=1, p=3, d=5, f=7.
const ORBITALS: Record<Sublevel, Orbital[]> = {
  s: [{ id: 's', label: 's', glyph: { kind: 'sphere' } }],
  p: [
    { id: 'px', label: 'pₓ', glyph: { kind: 'dumbbell', rot: 90 } },
    { id: 'py', label: 'p_y', glyph: { kind: 'dumbbell', rot: 0 } },
    { id: 'pz', label: 'p_z', glyph: { kind: 'dumbbell', rot: 0, depth: true } },
  ],
  d: [
    { id: 'dxy', label: 'd_xy', glyph: { kind: 'clover', rot: 45 } },
    { id: 'dxz', label: 'd_xz', glyph: { kind: 'clover', rot: 0 } },
    { id: 'dyz', label: 'd_yz', glyph: { kind: 'clover', rot: 90 } },
    { id: 'dx2y2', label: 'd_x²−y²', glyph: { kind: 'clover', rot: 0 } },
    { id: 'dz2', label: 'd_z²', glyph: { kind: 'donut' } },
  ],
  f: [
    { id: 'fz3', label: 'f_z³', glyph: { kind: 'emblem', lobes: 2 } },
    { id: 'fxz2', label: 'f_xz²', glyph: { kind: 'emblem', lobes: 4 } },
    { id: 'fyz2', label: 'f_yz²', glyph: { kind: 'emblem', lobes: 4 } },
    { id: 'fxyz', label: 'f_xyz', glyph: { kind: 'emblem', lobes: 8 } },
    { id: 'fz(x2y2)', label: 'f_z(x²−y²)', glyph: { kind: 'emblem', lobes: 6 } },
    { id: 'fx(x23y2)', label: 'f_x(x²−3y²)', glyph: { kind: 'emblem', lobes: 6 } },
    { id: 'fy(3x2y2)', label: 'f_y(3x²−y²)', glyph: { kind: 'emblem', lobes: 6 } },
  ],
};

const SUBTITLE: Record<Sublevel, string> = {
  s: 'One spherical orbital — holds 2 electrons.',
  p: 'Three dumbbell orbitals along x, y, z — hold 6 electrons.',
  d: 'Five orbitals: four cloverleaves + the d_z² — hold 10 electrons.',
  f: 'Seven complex orbitals — hold 14 electrons.',
};

// Render a small "label" string with subscripts/superscripts. Our labels embed
// markers: "_" before a subscript run, "²/³/−" already as unicode, and the p/d
// labels use the unicode subscripts directly where possible.
function OrbitalText({ label }: { label: string }) {
  // Split on '_' so the part after underscore is a subscript.
  const us = label.indexOf('_');
  if (us === -1) return <>{label}</>;
  return (
    <>
      {label.slice(0, us)}
      <tspan baselineShift="sub" fontSize="0.7em">
        {label.slice(us + 1)}
      </tspan>
    </>
  );
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

const VIEW = 260;
const C = VIEW / 2;
const AX = 96; // axis half-length

export default function OrbitalShapes({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);

  // Whether config pinned a single sublevel (no auto-cycle self-demo).
  const pinned = config.sublevel && SUBLEVELS.includes(config.sublevel);
  const initial: Sublevel = pinned ? (config.sublevel as Sublevel) : 's';

  const [sublevel, setSublevel] = useState<Sublevel>(initial);
  // Which orbital id is spotlighted (others dim). null ⇒ all equally lit.
  const [spotlight, setSpotlight] = useState<string | null>(null);
  const [angle, setAngle] = useState(0);
  const [showTry, setShowTry] = useState(false);
  const interactedRef = useRef(false);
  const demoTimers = useRef<number[]>([]);

  const orbitals = ORBITALS[sublevel];

  // Gentle rotation of the whole orbital group (paused under reduced motion).
  useAnimationFrame((dt) => setAngle((a) => (a + dt * 0.5) % (Math.PI * 2)), !reduced);

  // --- Self-demo: when not cue-driven, sweep through sublevels (or just the
  // orbitals of the pinned one), spotlighting each, then settle + "Now you try".
  useEffect(() => {
    const clearTimers = () => {
      demoTimers.current.forEach((t) => window.clearTimeout(t));
      demoTimers.current = [];
    };
    if (reduced || cueOn) {
      setShowTry(false);
      setSpotlight(null);
      setSublevel(initial);
      return clearTimers;
    }

    const tl: number[] = [];
    let at = 400;
    const STEP = 620;
    // The order we walk: pinned ⇒ that sublevel's orbitals; else all sublevels.
    const tour: Array<{ sub: Sublevel; id: string }> = pinned
      ? ORBITALS[initial].map((o) => ({ sub: initial, id: o.id }))
      : SUBLEVELS.map((s) => ({ sub: s, id: ORBITALS[s][0].id }));

    tour.forEach(({ sub, id }) => {
      tl.push(
        window.setTimeout(() => {
          setSublevel(sub);
          setSpotlight(id);
        }, at),
      );
      at += STEP;
    });
    // Settle: clear spotlight, land on the initial sublevel, offer the prompt.
    tl.push(
      window.setTimeout(() => {
        setSpotlight(null);
        setSublevel(initial);
        setShowTry(true);
      }, at),
    );
    demoTimers.current = tl;
    return clearTimers;
    // Arm once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Cue: highlight (sublevel or orbital id), step (walk orbitals), or
  // progress (sweep s→p→d→f). Supersedes the self-demo.
  useEffect(() => {
    if (!cue) return;
    demoTimers.current.forEach((t) => window.clearTimeout(t));
    demoTimers.current = [];
    setShowTry(false);

    const tokens = cueHighlights(cue); // lowercased
    if (tokens.length) {
      // Orbital id match (e.g. "px", "dz2") wins; else sublevel letter.
      for (const sub of SUBLEVELS) {
        const hit = ORBITALS[sub].find((o) => tokens.includes(o.id.toLowerCase()));
        if (hit) {
          setSublevel(sub);
          setSpotlight(hit.id);
          return;
        }
      }
      const subTok = tokens.find((t) => (SUBLEVELS as string[]).includes(t)) as
        | Sublevel
        | undefined;
      if (subTok) {
        setSublevel(subTok);
        setSpotlight(null);
        return;
      }
    }

    if (typeof cue.step === 'number') {
      const list = ORBITALS[sublevel];
      const i = clamp(Math.round(cue.step), 0, list.length - 1);
      setSpotlight(list[i].id);
      return;
    }

    if (typeof cue.progress === 'number') {
      const p = clamp(cue.progress, 0, 1);
      const idx = clamp(Math.round(p * (SUBLEVELS.length - 1)), 0, SUBLEVELS.length - 1);
      setSublevel(SUBLEVELS[idx]);
      setSpotlight(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue]);

  const markInteracted = (next: Sublevel) => {
    demoTimers.current.forEach((t) => window.clearTimeout(t));
    demoTimers.current = [];
    setShowTry(false);
    setSublevel(next);
    setSpotlight(null);
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'orbital_shapes', sublevel: next });
    }
  };

  // A stable per-render rotation in degrees for the orbital group.
  const rotDeg = (angle * 180) / Math.PI;

  return (
    <VizCard
      eyebrow="Quantum atom · Orbital shapes"
      title={config.title ?? 'Shapes of the s, p, d and f orbitals'}
      subtitle={SUBTITLE[sublevel]}
      reducedMotion={reduced}
      hint="Pick a sublevel to see the shape and orientation of each orbital it contains."
    >
      <div className="flex w-full flex-col gap-3">
        {/* Sublevel selector */}
        <div className="flex flex-wrap items-center gap-1.5">
          {SUBLEVELS.map((s) => {
            const active = s === sublevel;
            return (
              <button
                key={s}
                type="button"
                onClick={() => markInteracted(s)}
                aria-pressed={active}
                className={`rounded-sm border px-3 py-1 text-sm font-semibold transition-colors ${VIZ_FOCUS} ${
                  active
                    ? 'border-ws-accent bg-ws-accent text-ws-on-accent'
                    : 'border-ws-border bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
                }`}
              >
                {s}
                <span className="ml-1 text-[10px] font-normal opacity-70">
                  {ORBITALS[s].length}
                </span>
              </button>
            );
          })}
          <span className="ml-1 text-xs text-ws-ink-3">
            {orbitals.length} orbital{orbitals.length === 1 ? '' : 's'} · holds{' '}
            {orbitals.length * 2} e⁻
          </span>
        </div>

        {/* The orbital grid */}
        <div className="relative grid grid-cols-1 gap-2 rounded-md border border-ws-border bg-ws-surface-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
          <NowYouTry show={showTry && !reduced && !cueOn} />
          {orbitals.map((o) => {
            const dim = spotlight != null && spotlight !== o.id;
            const lit = spotlight === o.id;
            return (
              <OrbitalCell
                key={o.id}
                orbital={o}
                rotDeg={rotDeg}
                reduced={reduced}
                dim={dim}
                lit={lit}
                onClick={() => {
                  setSpotlight((cur) => (cur === o.id ? null : o.id));
                  if (!interactedRef.current) {
                    interactedRef.current = true;
                    onComplete?.({ type: 'orbital_shapes', sublevel, orbital: o.id });
                  }
                }}
              />
            );
          })}
        </div>
      </div>
    </VizCard>
  );
}

// ---------------------------------------------------------------------------
// A single orbital tile: an isometric x/y/z axis frame + the orbital glyph,
// with a caption. Spotlight (lit) brightens + scales slightly; non-spotlit
// tiles dim when something else is highlighted.
// ---------------------------------------------------------------------------
function OrbitalCell({
  orbital,
  rotDeg,
  reduced,
  dim,
  lit,
  onClick,
}: {
  orbital: Orbital;
  rotDeg: number;
  reduced: boolean;
  dim: boolean;
  lit: boolean;
  onClick: () => void;
}) {
  // Unique gradient ids per orbital so multiple tiles don't collide.
  const gid = useMemo(() => `og-${orbital.id.replace(/[^a-z0-9]/gi, '')}`, [orbital.id]);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${orbital.id} orbital`}
      className={`group relative flex flex-col items-center rounded-md border bg-ws-surface p-2 transition-all ${VIZ_FOCUS} ${
        lit ? 'border-ws-accent shadow-sm' : 'border-ws-border hover:border-ws-border-strong'
      }`}
      style={{
        opacity: dim ? 0.4 : 1,
        transform: lit && !reduced ? 'scale(1.02)' : 'scale(1)',
        transition: reduced ? undefined : 'opacity 300ms ease, transform 300ms ease, border-color 200ms ease',
      }}
    >
      <svg
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        className="w-full max-w-[200px]"
        role="img"
        aria-label={`${orbital.id} orbital shape`}
      >
        <OrbitalGradients gid={gid} lit={lit} />
        {/* Axis frame (drawn behind the orbital). */}
        <AxisFrame />
        {/* The rotating orbital glyph. */}
        <g
          transform={`rotate(${reduced ? 0 : rotDeg * 0.15} ${C} ${C})`}
          style={{ transition: reduced ? undefined : 'opacity 200ms ease' }}
        >
          <Glyphs glyph={orbital.glyph} gid={gid} />
        </g>
      </svg>
      <span className="mt-1 font-mono text-xs text-ws-ink-2">
        <svg viewBox="0 0 80 18" className="inline-block h-4 w-auto align-middle" aria-hidden>
          <text x="0" y="13" className="fill-current font-mono" fontSize="13">
            <OrbitalText label={orbital.label} />
          </text>
        </svg>
      </span>
    </button>
  );
}

// Shared radial gradients for filled lobes (accent-tinted) and the alternate
// phase lobe (a cooler tone so +/- lobes read distinctly), plus a sphere shade.
function OrbitalGradients({ gid, lit }: { gid: string; lit: boolean }) {
  return (
    <defs>
      <radialGradient id={`${gid}-a`} cx="35%" cy="30%" r="80%">
        <stop offset="0%" stopColor="var(--ws-accent)" stopOpacity={lit ? 0.95 : 0.8} />
        <stop offset="100%" stopColor="var(--ws-accent)" stopOpacity={lit ? 0.55 : 0.4} />
      </radialGradient>
      <radialGradient id={`${gid}-b`} cx="35%" cy="30%" r="80%">
        <stop offset="0%" stopColor="var(--ws-ink-3)" stopOpacity={lit ? 0.7 : 0.5} />
        <stop offset="100%" stopColor="var(--ws-ink-3)" stopOpacity={lit ? 0.4 : 0.25} />
      </radialGradient>
      <radialGradient id={`${gid}-s`} cx="38%" cy="32%" r="75%">
        <stop offset="0%" stopColor="var(--ws-accent)" stopOpacity={lit ? 0.85 : 0.65} />
        <stop offset="70%" stopColor="var(--ws-accent)" stopOpacity={lit ? 0.5 : 0.35} />
        <stop offset="100%" stopColor="var(--ws-accent)" stopOpacity={0.18} />
      </radialGradient>
    </defs>
  );
}

// The isometric x/y/z axis frame: x to the right, y up, z toward the viewer
// (drawn diagonally up-left to suggest depth). Light, behind the orbital.
function AxisFrame() {
  const zdx = -AX * 0.62;
  const zdy = -AX * 0.5;
  const axis = (x2: number, y2: number, label: string, lx: number, ly: number) => (
    <g key={label}>
      <line
        x1={C}
        y1={C}
        x2={C + x2}
        y2={C + y2}
        stroke="var(--ws-border-strong)"
        strokeWidth="1"
        strokeDasharray="2 3"
      />
      <text
        x={C + lx}
        y={C + ly}
        fontSize="11"
        className="fill-ws-ink-3 font-mono"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {label}
      </text>
    </g>
  );
  return (
    <g>
      {/* negative directions (fainter) */}
      <line x1={C} y1={C} x2={C - AX} y2={C} stroke="var(--ws-border)" strokeWidth="1" strokeDasharray="2 3" />
      <line x1={C} y1={C} x2={C} y2={C + AX} stroke="var(--ws-border)" strokeWidth="1" strokeDasharray="2 3" />
      <line x1={C} y1={C} x2={C - zdx} y2={C - zdy} stroke="var(--ws-border)" strokeWidth="1" strokeDasharray="2 3" />
      {/* positive directions + labels */}
      {axis(AX, 0, 'x', AX + 10, 0)}
      {axis(0, -AX, 'y', 0, -AX - 10)}
      {axis(zdx, zdy, 'z', zdx - 10, zdy - 2)}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Glyph renderers. Coordinates are centered on (C, C). A single dumbbell lobe
// is a teardrop path pointing outward; we mirror it for the opposite lobe and
// color the two lobes differently to show the +/- phases.
// ---------------------------------------------------------------------------

// A dumbbell (two opposing lobes) pointing up/down by default; `rot` rotates it.
// `depth` foreshortens it (z axis pointing into the screen) and skews it along
// the isometric depth direction.
function Dumbbell({ gid, rot, depth }: { gid: string; rot: number; depth?: boolean }) {
  const len = 78;
  const w = 30;
  // One teardrop lobe from center outward (upwards) using cubic curves.
  const lobe = `M ${C} ${C}
    C ${C - w} ${C - len * 0.4}, ${C - w} ${C - len * 0.95}, ${C} ${C - len}
    C ${C + w} ${C - len * 0.95}, ${C + w} ${C - len * 0.4}, ${C} ${C} Z`;
  const transform = depth
    ? `rotate(-38 ${C} ${C}) scale(1 0.55) `
    : `rotate(${rot} ${C} ${C})`;
  return (
    <g transform={transform}>
      <path d={lobe} fill={`url(#${gid}-a)`} stroke="var(--ws-accent)" strokeWidth="1" />
      <path
        d={lobe}
        fill={`url(#${gid}-b)`}
        stroke="var(--ws-ink-3)"
        strokeWidth="1"
        transform={`rotate(180 ${C} ${C})`}
      />
      <circle cx={C} cy={C} r="2.5" fill="var(--ws-ink-2)" />
    </g>
  );
}

// A cloverleaf (four lobes at 90°). `rot` tilts the whole flower.
function Clover({ gid, rot }: { gid: string; rot: number }) {
  const len = 64;
  const w = 22;
  const lobe = `M ${C} ${C}
    C ${C - w} ${C - len * 0.45}, ${C - w} ${C - len * 0.95}, ${C} ${C - len}
    C ${C + w} ${C - len * 0.95}, ${C + w} ${C - len * 0.45}, ${C} ${C} Z`;
  // Alternate phase color around the flower (+ / − / + / −).
  return (
    <g transform={`rotate(${rot} ${C} ${C})`}>
      {[0, 90, 180, 270].map((a, i) => (
        <path
          key={a}
          d={lobe}
          transform={`rotate(${a} ${C} ${C})`}
          fill={`url(#${gid}-${i % 2 === 0 ? 'a' : 'b'})`}
          stroke={i % 2 === 0 ? 'var(--ws-accent)' : 'var(--ws-ink-3)'}
          strokeWidth="1"
        />
      ))}
      <circle cx={C} cy={C} r="2.5" fill="var(--ws-ink-2)" />
    </g>
  );
}

// The d_z² orbital: two vertical lobes (z axis) wrapped by a toroidal ring in
// the xy plane (drawn as a flattened ellipse for the doughnut).
function Donut({ gid }: { gid: string }) {
  const len = 70;
  const w = 24;
  const lobe = `M ${C} ${C}
    C ${C - w} ${C - len * 0.4}, ${C - w} ${C - len * 0.95}, ${C} ${C - len}
    C ${C + w} ${C - len * 0.95}, ${C + w} ${C - len * 0.4}, ${C} ${C} Z`;
  return (
    <g>
      {/* toroidal ring (the "doughnut" in the xy plane) */}
      <ellipse
        cx={C}
        cy={C}
        rx="52"
        ry="20"
        fill={`url(#${gid}-b)`}
        stroke="var(--ws-ink-3)"
        strokeWidth="1"
        opacity="0.85"
      />
      <ellipse cx={C} cy={C} rx="26" ry="9" fill="var(--ws-surface)" opacity="0.9" />
      {/* the two vertical lobes */}
      <path d={lobe} fill={`url(#${gid}-a)`} stroke="var(--ws-accent)" strokeWidth="1" />
      <path
        d={lobe}
        fill={`url(#${gid}-a)`}
        stroke="var(--ws-accent)"
        strokeWidth="1"
        transform={`rotate(180 ${C} ${C})`}
      />
      <circle cx={C} cy={C} r="2.5" fill="var(--ws-ink-2)" />
    </g>
  );
}

// f-orbital emblem: a ring of `lobes` slim petals, alternating phase. f shapes
// are genuinely complex 3D; this stylized multi-lobe rosette conveys "many
// lobes, high angular momentum" without pretending to be a precise contour.
function Emblem({ gid, lobes }: { gid: string; lobes: number }) {
  const len = 58;
  const w = 12;
  const lobe = `M ${C} ${C}
    C ${C - w} ${C - len * 0.45}, ${C - w} ${C - len * 0.95}, ${C} ${C - len}
    C ${C + w} ${C - len * 0.95}, ${C + w} ${C - len * 0.45}, ${C} ${C} Z`;
  const step = 360 / lobes;
  return (
    <g>
      {Array.from({ length: lobes }).map((_, i) => (
        <path
          key={i}
          d={lobe}
          transform={`rotate(${i * step} ${C} ${C})`}
          fill={`url(#${gid}-${i % 2 === 0 ? 'a' : 'b'})`}
          stroke={i % 2 === 0 ? 'var(--ws-accent)' : 'var(--ws-ink-3)'}
          strokeWidth="0.75"
        />
      ))}
      <circle cx={C} cy={C} r="2.5" fill="var(--ws-ink-2)" />
    </g>
  );
}

function Glyphs({ glyph, gid }: { glyph: Glyph; gid: string }) {
  switch (glyph.kind) {
    case 'sphere':
      return (
        <g>
          <circle cx={C} cy={C} r="58" fill={`url(#${gid}-s)`} stroke="var(--ws-accent)" strokeWidth="1" />
          {/* a faint highlight to read as a 3D ball */}
          <ellipse cx={C - 18} cy={C - 20} rx="16" ry="10" fill="var(--ws-surface)" opacity="0.25" />
          <circle cx={C} cy={C} r="2.5" fill="var(--ws-ink-2)" />
        </g>
      );
    case 'dumbbell':
      return <Dumbbell gid={gid} rot={glyph.rot} depth={glyph.depth} />;
    case 'clover':
      return <Clover gid={gid} rot={glyph.rot} />;
    case 'donut':
      return <Donut gid={gid} />;
    case 'emblem':
      return <Emblem gid={gid} lobes={glyph.lobes} />;
    default:
      return null;
  }
}
